'use client';

import React, { useState, useCallback } from 'react';
import { FileDown, Loader2, CheckCircle2 } from 'lucide-react';
import { useSession } from './SessionProvider';
import { AIPrescription } from '@/lib/services/adaptiveProtocolService';
import toast from 'react-hot-toast';

interface ClinicianReportExportProps {
  prescription?: AIPrescription | null;
  sessionId?: string;
}

// ─── PDF Generation ────────────────────────────────────────────────────────────
async function generateClinicianPDF(
  metrics: ReturnType<typeof useSession>['metrics'],
  prescription: AIPrescription | null | undefined,
  sessionId?: string
): Promise<void> {
  // Dynamic import to avoid SSR issues
  const { default: jsPDF } = await import('jspdf');

  const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();
  const margin = 18;
  const contentW = pageW - margin * 2;
  let y = margin;

  // ── Colour palette ──────────────────────────────────────────────────────────
  const C = {
    orange: [249, 115, 22] as [number, number, number],
    navy: [30, 58, 95] as [number, number, number],
    dark: [11, 22, 40] as [number, number, number],
    white: [255, 255, 255] as [number, number, number],
    lightGray: [245, 247, 250] as [number, number, number],
    midGray: [120, 130, 150] as [number, number, number],
    red: [239, 68, 68] as [number, number, number],
    green: [34, 197, 94] as [number, number, number],
    amber: [251, 191, 36] as [number, number, number],
  };

  // ── Helper: new page check ──────────────────────────────────────────────────
  const checkPage = (needed: number) => {
    if (y + needed > pageH - margin) {
      doc.addPage();
      y = margin;
    }
  };

  // ── Helper: section header ──────────────────────────────────────────────────
  const sectionHeader = (title: string) => {
    checkPage(12);
    doc.setFillColor(...C.navy);
    doc.roundedRect(margin, y, contentW, 8, 1, 1, 'F');
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(9);
    doc.setTextColor(...C.white);
    doc.text(title.toUpperCase(), margin + 4, y + 5.5);
    y += 12;
  };

  // ── Helper: key-value row ───────────────────────────────────────────────────
  const kvRow = (label: string, value: string, highlight = false) => {
    checkPage(7);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8.5);
    doc.setTextColor(...C.midGray);
    doc.text(label, margin + 2, y);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(highlight ? C.orange[0] : C.dark[0], highlight ? C.orange[1] : C.dark[1], highlight ? C.orange[2] : C.dark[2]);
    doc.text(value, margin + 60, y);
    y += 6;
  };

  // ── Helper: wrapped text ────────────────────────────────────────────────────
  const wrappedText = (text: string, fontSize = 9, color: [number, number, number] = C.dark) => {
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(fontSize);
    doc.setTextColor(...color);
    const lines = doc.splitTextToSize(text, contentW - 4);
    lines.forEach((line: string) => {
      checkPage(6);
      doc.text(line, margin + 2, y);
      y += 5.5;
    });
    y += 2;
  };

  // ══════════════════════════════════════════════════════════════════════════
  // COVER HEADER
  // ══════════════════════════════════════════════════════════════════════════
  // Orange accent bar
  doc.setFillColor(...C.orange);
  doc.rect(0, 0, pageW, 3, 'F');

  // Dark header background
  doc.setFillColor(...C.dark);
  doc.rect(0, 3, pageW, 38, 'F');

  // Title
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(18);
  doc.setTextColor(...C.orange);
  doc.text('iBrain LAB', margin, 20);
  doc.setFontSize(10);
  doc.setTextColor(...C.white);
  doc.text('EEG Cognitive Diagnostic Report', margin, 28);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8);
  doc.setTextColor(138, 175, 212);
  doc.text(`Generated: ${new Date().toLocaleString()}`, margin, 35);
  if (sessionId) {
    doc.text(`Session ID: ${sessionId}`, pageW - margin - 60, 35);
  }

  y = 52;

  // ══════════════════════════════════════════════════════════════════════════
  // SECTION 1 — SESSION OVERVIEW
  // ══════════════════════════════════════════════════════════════════════════
  sectionHeader('1. Session Overview');

  const tbr = metrics.thetaPower / Math.max(metrics.betaPower, 0.1);
  const tar = metrics.thetaPower / Math.max(metrics.alphaPower, 0.1);
  const dar = metrics.deltaPower / Math.max(metrics.alphaPower, 0.1);
  const engagementIndex = metrics.betaPower / (metrics.alphaPower + metrics.thetaPower);

  kvRow('Subject ID', 'anonymous');
  kvRow('Signal Quality', `${metrics.signalQualityIndex.toFixed(1)}%`, metrics.signalQualityIndex < 70);
  kvRow('Artifact Rate', `${metrics.artifactRate.toFixed(2)}/min`, metrics.artifactRate > 3);
  kvRow('Cognitive Load Index', `${metrics.cognitiveLoadIndex.toFixed(1)}/100`, metrics.cognitiveLoadIndex > 80);
  kvRow('Attention Score', `${metrics.attentionScore.toFixed(1)}/100`, metrics.attentionScore < 45);
  y += 2;

  // ══════════════════════════════════════════════════════════════════════════
  // SECTION 2 — BAND POWER ANALYSIS
  // ══════════════════════════════════════════════════════════════════════════
  sectionHeader('2. EEG Band Power Analysis (µV²)');

  const bands = [
    { name: 'Delta (0.5–4 Hz)', value: metrics.deltaPower, norm: '2–25', flag: metrics.deltaPower > 25 || metrics.deltaPower < 2 },
    { name: 'Theta (4–8 Hz)', value: metrics.thetaPower, norm: '3–20', flag: metrics.thetaPower > 20 },
    { name: 'Alpha (8–13 Hz)', value: metrics.alphaPower, norm: '8–35', flag: metrics.alphaPower < 8 || metrics.alphaPower > 35 },
    { name: 'Beta (13–30 Hz)', value: metrics.betaPower, norm: '2–18', flag: metrics.betaPower > 18 },
    { name: 'Gamma (30–100 Hz)', value: metrics.gammaPower, norm: '1–10', flag: metrics.gammaPower > 10 },
  ];

  // Table header
  doc.setFillColor(...C.lightGray);
  doc.rect(margin, y, contentW, 6.5, 'F');
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(8);
  doc.setTextColor(...C.dark);
  doc.text('Band', margin + 2, y + 4.5);
  doc.text('Value', margin + 60, y + 4.5);
  doc.text('Norm Range', margin + 90, y + 4.5);
  doc.text('Status', margin + 130, y + 4.5);
  y += 7.5;

  bands.forEach((b, i) => {
    checkPage(7);
    if (i % 2 === 0) {
      doc.setFillColor(250, 251, 253);
      doc.rect(margin, y - 1, contentW, 6.5, 'F');
    }
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8.5);
    doc.setTextColor(...C.dark);
    doc.text(b.name, margin + 2, y + 4);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(b.flag ? C.red[0] : C.dark[0], b.flag ? C.red[1] : C.dark[1], b.flag ? C.red[2] : C.dark[2]);
    doc.text(`${b.value.toFixed(2)} µV²`, margin + 60, y + 4);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...C.midGray);
    doc.text(b.norm, margin + 90, y + 4);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(b.flag ? C.red[0] : C.green[0], b.flag ? C.red[1] : C.green[1], b.flag ? C.red[2] : C.green[2]);
    doc.text(b.flag ? '⚠ DEVIATION' : '✓ NORMAL', margin + 130, y + 4);
    y += 6.5;
  });
  y += 4;

  // ══════════════════════════════════════════════════════════════════════════
  // SECTION 3 — CLINICAL RATIOS
  // ══════════════════════════════════════════════════════════════════════════
  sectionHeader('3. Clinical Biomarker Ratios');

  const alzheimerRisk = tar > 2.0 ? 'Severe' : tar > 1.6 ? 'Moderate' : tar > 1.2 ? 'Mild' : 'Low';
  const adhdClass = tbr > 3.5 ? 'High' : tbr > 2.5 ? 'Elevated' : tbr > 1.8 ? 'Borderline' : 'Low';

  kvRow('Theta/Alpha Ratio (TAR)', `${tar.toFixed(3)}  →  Alzheimer Risk: ${alzheimerRisk}`, tar > 1.6);
  kvRow('Theta/Beta Ratio (TBR)', `${tbr.toFixed(3)}  →  ADHD Classification: ${adhdClass}`, tbr > 2.5);
  kvRow('Delta/Alpha Ratio (DAR)', `${dar.toFixed(3)}`, dar > 2.0);
  kvRow('Engagement Index', `${engagementIndex.toFixed(3)}`, engagementIndex < 0.3);
  kvRow('Inter-Hemispheric Coherence', `${metrics.interHemisphericCoherence.toFixed(1)}%`, metrics.interHemisphericCoherence < 55);
  y += 4;

  // ══════════════════════════════════════════════════════════════════════════
  // SECTION 4 — AI PRESCRIPTION
  // ══════════════════════════════════════════════════════════════════════════
  if (prescription) {
    sectionHeader('4. AI Clinical Prescription (GPT-5)');

    // Urgency badge
    const urgencyColors: Record<string, [number, number, number]> = {
      ROUTINE: C.green,
      ELEVATED: C.amber,
      CRITICAL: C.red,
    };
    const uc = urgencyColors[prescription.urgencyLevel] ?? C.green;
    doc.setFillColor(uc[0], uc[1], uc[2]);
    doc.roundedRect(margin, y, 40, 7, 1, 1, 'F');
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(8);
    doc.setTextColor(...C.white);
    doc.text(prescription.urgencyLevel, margin + 4, y + 5);
    y += 11;

    // Summary
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(9);
    doc.setTextColor(...C.dark);
    doc.text('Clinical Summary:', margin + 2, y);
    y += 5;
    wrappedText(prescription.summary, 9, C.dark);

    // Follow-up
    if (prescription.followUpRecommended && prescription.followUpReason) {
      checkPage(12);
      doc.setFillColor(249, 115, 22, 0.1);
      doc.setDrawColor(...C.orange);
      doc.roundedRect(margin, y, contentW, 10, 1, 1, 'S');
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(8.5);
      doc.setTextColor(...C.orange);
      doc.text('⚠ Clinician Follow-up Recommended:', margin + 3, y + 4);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(...C.dark);
      const followLines = doc.splitTextToSize(prescription.followUpReason, contentW - 8);
      doc.text(followLines[0] ?? '', margin + 3, y + 8.5);
      y += 14;
    }

    // Exercises table
    if (prescription.exercises.length > 0) {
      checkPage(20);
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(9);
      doc.setTextColor(...C.dark);
      doc.text('Prescribed NeuroTrainer Exercises:', margin + 2, y);
      y += 5;

      doc.setFillColor(...C.lightGray);
      doc.rect(margin, y, contentW, 6.5, 'F');
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(8);
      doc.setTextColor(...C.dark);
      doc.text('#', margin + 2, y + 4.5);
      doc.text('Exercise', margin + 10, y + 4.5);
      doc.text('Priority', margin + 70, y + 4.5);
      doc.text('Difficulty', margin + 95, y + 4.5);
      doc.text('Duration', margin + 130, y + 4.5);
      y += 7.5;

      prescription.exercises.forEach((ex, i) => {
        checkPage(14);
        if (i % 2 === 0) {
          doc.setFillColor(250, 251, 253);
          doc.rect(margin, y - 1, contentW, 13, 'F');
        }
        const pColor: Record<string, [number, number, number]> = {
          HIGH: C.red, MED: C.orange, LOW: C.green,
        };
        const pc = pColor[ex.priority] ?? C.midGray;

        doc.setFont('helvetica', 'bold');
        doc.setFontSize(8.5);
        doc.setTextColor(...C.dark);
        doc.text(`${i + 1}`, margin + 2, y + 4);
        doc.text(ex.name, margin + 10, y + 4);
        doc.setTextColor(...pc);
        doc.text(ex.priority, margin + 70, y + 4);
        doc.setTextColor(...C.dark);
        doc.setFont('helvetica', 'normal');
        doc.text(ex.difficulty, margin + 95, y + 4);
        doc.text(ex.duration, margin + 130, y + 4);
        // Rationale
        doc.setFontSize(7.5);
        doc.setTextColor(...C.midGray);
        const rationale = doc.splitTextToSize(ex.rationale, contentW - 12);
        doc.text(rationale[0] ?? '', margin + 10, y + 9);
        y += 13;
      });
      y += 4;
    }

    // Lifestyle
    if (prescription.lifestyle.length > 0) {
      checkPage(16);
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(9);
      doc.setTextColor(...C.dark);
      doc.text('Lifestyle Interventions:', margin + 2, y);
      y += 5;

      prescription.lifestyle.forEach((item, i) => {
        checkPage(16);
        if (i % 2 === 0) {
          doc.setFillColor(250, 251, 253);
          doc.rect(margin, y - 1, contentW, 14, 'F');
        }
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(8.5);
        doc.setTextColor(...C.navy);
        doc.text(`[${item.category}]`, margin + 2, y + 4);
        doc.setTextColor(...C.dark);
        const recLines = doc.splitTextToSize(item.recommendation, contentW - 30);
        doc.text(recLines[0] ?? '', margin + 28, y + 4);
        doc.setFont('helvetica', 'italic');
        doc.setFontSize(7.5);
        doc.setTextColor(...C.midGray);
        const evLines = doc.splitTextToSize(item.evidence, contentW - 10);
        doc.text(evLines[0] ?? '', margin + 2, y + 9.5);
        y += 14;
      });
    }
  } else {
    sectionHeader('4. AI Clinical Prescription');
    wrappedText('No AI prescription was generated for this session. Run the Post-Diagnostic AI Prescription Engine to generate a clinical action plan.', 9, C.midGray);
  }

  // ══════════════════════════════════════════════════════════════════════════
  // FOOTER on each page
  // ══════════════════════════════════════════════════════════════════════════
  const totalPages = (doc.internal as { getNumberOfPages: () => number }).getNumberOfPages();
  for (let p = 1; p <= totalPages; p++) {
    doc.setPage(p);
    doc.setFillColor(...C.dark);
    doc.rect(0, pageH - 10, pageW, 10, 'F');
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(7.5);
    doc.setTextColor(138, 175, 212);
    doc.text('iBrain LAB — EEG Cognitive Diagnostic Report — Confidential Clinical Document', margin, pageH - 4);
    doc.text(`Page ${p} of ${totalPages}`, pageW - margin - 18, pageH - 4);
  }

  // Save
  const filename = `iBrainLAB_Report_${new Date().toISOString().slice(0, 10)}.pdf`;
  doc.save(filename);
}

// ─── Main Component ────────────────────────────────────────────────────────────
export default function ClinicianReportExport({ prescription, sessionId }: ClinicianReportExportProps) {
  const { metrics } = useSession();
  const [generating, setGenerating] = useState(false);
  const [done, setDone] = useState(false);

  const handleExport = useCallback(async () => {
    setGenerating(true);
    setDone(false);
    try {
      await generateClinicianPDF(metrics, prescription, sessionId);
      setDone(true);
      toast.success('PDF report downloaded');
      setTimeout(() => setDone(false), 3000);
    } catch (err) {
      console.error('PDF generation error:', err);
      toast.error('Failed to generate PDF');
    } finally {
      setGenerating(false);
    }
  }, [metrics, prescription, sessionId]);

  return (
    <button
      onClick={handleExport}
      disabled={generating}
      className="flex items-center gap-2 px-4 py-2.5 rounded-xl text-[13px] font-semibold transition-all touch-manipulation active:scale-95 disabled:opacity-60"
      style={{
        background: done
          ? 'linear-gradient(135deg, rgba(34,197,94,0.2), rgba(34,197,94,0.1))'
          : 'linear-gradient(135deg, rgba(30,58,95,0.6), rgba(11,22,40,0.8))',
        border: done ? '1px solid rgba(34,197,94,0.4)' : '1px solid rgba(30,58,95,0.6)',
        color: done ? '#22C55E' : '#8BAFD4',
        boxShadow: done ? '0 0 16px rgba(34,197,94,0.1)' : 'none',
      }}
      title="Export full diagnostic + AI prescription as PDF"
    >
      {generating ? (
        <Loader2 size={14} className="animate-spin" />
      ) : done ? (
        <CheckCircle2 size={14} />
      ) : (
        <FileDown size={14} />
      )}
      {generating ? 'Generating PDF…' : done ? 'Downloaded!' : 'Export Clinician PDF'}
    </button>
  );
}

// ─── Standalone export function for use in PostDiagnosticPrescription ─────────
export async function exportClinicianPDF(
  metrics: ReturnType<typeof useSession>['metrics'],
  prescription: AIPrescription | null | undefined,
  sessionId?: string
): Promise<void> {
  return generateClinicianPDF(metrics, prescription, sessionId);
}
